import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import chitalishteAdminService from '../../services/chitalishteAdminService';

export const fetchAllChitalishta = createAsyncThunk(
  'chitalishteAdmin/fetchAll',
  async (params = { page: 0, size: 12, sortBy: 'name', sortDirection: 'asc' }, { rejectWithValue }) => {
    try {
      const data = await chitalishteAdminService.getAllChitalishta(params);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Грешка при зареждане на читалища');
    }
  }
);

export const fetchChitalishteById = createAsyncThunk(
  'chitalishteAdmin/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const data = await chitalishteAdminService.getChitalishteById(id);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Грешка при зареждане на читалище');
    }
  }
);

export const createChitalishte = createAsyncThunk(
  'chitalishteAdmin/create',
  async ({ data, feedbackId }, { rejectWithValue }) => {
    try {
      const result = await chitalishteAdminService.createChitalishte(data, feedbackId);
      return result;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Грешка при създаване на читалище');
    }
  }
);

export const updateChitalishte = createAsyncThunk(
  'chitalishteAdmin/update',
  async ({ id, data, feedbackId }, { rejectWithValue }) => {
    try {
      const result = await chitalishteAdminService.updateChitalishte(id, data, feedbackId);
      return result;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Грешка при обновяване на читалище');
    }
  }
);

export const deleteChitalishte = createAsyncThunk(
  'chitalishteAdmin/delete',
  async ({ id, feedbackId }, { rejectWithValue }) => {
    try {
      await chitalishteAdminService.deleteChitalishte(id, feedbackId);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Грешка при изтриване на читалище');
    }
  }
);

export const fetchInformationCardsByChitalishte = createAsyncThunk(
  'chitalishteAdmin/fetchCards',
  async (chitalishteId, { rejectWithValue }) => {
    try {
      const data = await chitalishteAdminService.getInformationCardsByChitalishte(chitalishteId);
      return { chitalishteId, cards: data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Грешка при зареждане на информационни карти');
    }
  }
);

export const createInformationCard = createAsyncThunk(
  'chitalishteAdmin/createCard',
  async ({ data, feedbackId }, { rejectWithValue }) => {
    try {
      const result = await chitalishteAdminService.createInformationCard(data, feedbackId);
      return result;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Грешка при създаване на информационна карта');
    }
  }
);

export const updateInformationCard = createAsyncThunk(
  'chitalishteAdmin/updateCard',
  async ({ id, data, feedbackId }, { rejectWithValue }) => {
    try {
      const result = await chitalishteAdminService.updateInformationCard(id, data, feedbackId);
      return result;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Грешка при обновяване на информационна карта');
    }
  }
);

export const deleteInformationCard = createAsyncThunk(
  'chitalishteAdmin/deleteCard',
  async ({ id, feedbackId }, { rejectWithValue }) => {
    try {
      await chitalishteAdminService.deleteInformationCard(id, feedbackId);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Грешка при изтриване на информационна карта');
    }
  }
);

const chitalishteAdminSlice = createSlice({
  name: 'chitalishteAdmin',
  initialState: {
    chitalishta: [],
    informationCardsByChitalishte: {},
    loading: false,
    cardsLoading: false,
    error: null,
    cardsError: null,
    pagination: {
      page: 0,
      size: 12,
      totalPages: 0,
      totalElements: 0
    }
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.cardsError = null;
    },
    resetChitalishteState: (state) => {
      state.chitalishta = [];
      state.informationCardsByChitalishte = {};
      state.error = null;
      state.cardsError = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllChitalishta.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllChitalishta.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.content) {
          state.chitalishta = action.payload.content;
          state.pagination = {
            page: action.payload.pageable?.pageNumber || 0,
            size: action.payload.pageable?.pageSize || 12,
            totalPages: action.payload.totalPages || 1,
            totalElements: action.payload.totalElements || 0
          };
        } else {
          state.chitalishta = action.payload || [];
        }
      })
      .addCase(fetchAllChitalishta.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchChitalishteById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChitalishteById.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.chitalishta.findIndex(c => c.id === action.payload.id);
        if (index !== -1) {
          state.chitalishta[index] = action.payload;
        } else {
          state.chitalishta.push(action.payload);
        }
      })
      .addCase(fetchChitalishteById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createChitalishte.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createChitalishte.fulfilled, (state, action) => {
        state.loading = false;
        state.chitalishta.push(action.payload);
      })
      .addCase(createChitalishte.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateChitalishte.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateChitalishte.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.chitalishta.findIndex(c => c.id === action.payload.id);
        if (index !== -1) {
          state.chitalishta[index] = action.payload;
        }
      })
      .addCase(updateChitalishte.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteChitalishte.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteChitalishte.fulfilled, (state, action) => {
        state.loading = false;
        state.chitalishta = state.chitalishta.filter(c => c.id !== action.payload);
        delete state.informationCardsByChitalishte[action.payload];
      })
      .addCase(deleteChitalishte.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchInformationCardsByChitalishte.pending, (state) => {
        state.cardsLoading = true;
        state.cardsError = null;
      })
      .addCase(fetchInformationCardsByChitalishte.fulfilled, (state, action) => {
        state.cardsLoading = false;
        state.informationCardsByChitalishte[action.payload.chitalishteId] = action.payload.cards;
      })
      .addCase(fetchInformationCardsByChitalishte.rejected, (state, action) => {
        state.cardsLoading = false;
        state.cardsError = action.payload;
      })
      .addCase(createInformationCard.pending, (state) => {
        state.cardsLoading = true;
        state.cardsError = null;
      })
      .addCase(createInformationCard.fulfilled, (state, action) => {
        state.cardsLoading = false;
        const chitalishteId = action.payload.chitalishte?.id;
        if (chitalishteId) {
          if (!state.informationCardsByChitalishte[chitalishteId]) {
            state.informationCardsByChitalishte[chitalishteId] = [];
          }
          state.informationCardsByChitalishte[chitalishteId].push(action.payload);
        }
      })
      .addCase(createInformationCard.rejected, (state, action) => {
        state.cardsLoading = false;
        state.cardsError = action.payload;
      })
      .addCase(updateInformationCard.pending, (state) => {
        state.cardsLoading = true;
        state.cardsError = null;
      })
      .addCase(updateInformationCard.fulfilled, (state, action) => {
        state.cardsLoading = false;
        const chitalishteId = action.payload.chitalishte?.id;
        if (chitalishteId && state.informationCardsByChitalishte[chitalishteId]) {
          const index = state.informationCardsByChitalishte[chitalishteId].findIndex(
            card => card.id === action.payload.id
          );
          if (index !== -1) {
            state.informationCardsByChitalishte[chitalishteId][index] = action.payload;
          }
        }
      })
      .addCase(updateInformationCard.rejected, (state, action) => {
        state.cardsLoading = false;
        state.cardsError = action.payload;
      })
      .addCase(deleteInformationCard.pending, (state) => {
        state.cardsLoading = true;
        state.cardsError = null;
      })
      .addCase(deleteInformationCard.fulfilled, (state, action) => {
        state.cardsLoading = false;
        Object.keys(state.informationCardsByChitalishte).forEach(chitalishteId => {
          state.informationCardsByChitalishte[chitalishteId] = 
            state.informationCardsByChitalishte[chitalishteId].filter(
              card => card.id !== action.payload
            );
        });
      })
      .addCase(deleteInformationCard.rejected, (state, action) => {
        state.cardsLoading = false;
        state.cardsError = action.payload;
      });
  }
});

export const { clearError, resetChitalishteState } = chitalishteAdminSlice.actions;

export const selectAllChitalishta = (state) => state.chitalishteAdmin.chitalishta || [];
export const selectChitalishtaLoading = (state) => state.chitalishteAdmin.loading;
export const selectChitalishtaError = (state) => state.chitalishteAdmin.error;
export const selectInformationCardsByChitalishteId = (chitalishteId) => (state) => 
  state.chitalishteAdmin.informationCardsByChitalishte[chitalishteId] || [];
export const selectInformationCardsLoading = (state) => state.chitalishteAdmin.cardsLoading;
export const selectInformationCardsError = (state) => state.chitalishteAdmin.cardsError;
export const selectChitalishtaPagination = (state) => state.chitalishteAdmin.pagination;

export default chitalishteAdminSlice.reducer;